import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  Heading,
  Divider,
  SimpleGrid,
  IconButton,
  Textarea,
  NumberInput,
  NumberInputField,
  useColorModeValue,
  List,
  ListItem,
  InputGroup,
  InputRightElement,
  Text,
  Select,
  useToast,
} from '@chakra-ui/react';
import { AddIcon, ChevronDownIcon, DeleteIcon } from '@chakra-ui/icons';
import { getData } from '../../utils/httpClient.js';
import AddCustomerModal from './AddCustomerModal.js';

const mapInvoiceTotals = (inv) => {
  if (inv.totales) return inv.totales;
  const t = inv.invoiceTotals;
  if (!t) return null;
  const total = parseFloat(t.total || t.totalPayable || 0);
  const iva = parseFloat(t.basicVatAmount || t.vatAmount || 0);
  const rate = parseFloat(t.basicVatRate || 22);
  return {
    total,
    iva_basico: total - iva,
    mnt_iva_tasa_basico: iva,
    tasa_iva_basico: rate,
  };
};

const mapLocalInvoiceForCreditNote = (inv) => {
  if (!inv) return null;
  if (inv.serie && inv.numero && inv.items?.length) return inv;

  const items = (inv.invoiceItems || inv.items || []).map((item) => ({
    concepto: item.description || item.concepto || '',
    unidad: item.unit || item.unidad || 'un',
    cantidad: parseFloat(item.quantity || item.cantidad) || 1,
    precio: parseFloat(item.price || item.precio) || 0,
    cod_item: item.cod_item || item.code || '',
    indicador_facturacion: item.billingIndicator ?? item.indicador_facturacion ?? 3,
  }));

  const customer = inv.customer;
  const cliente =
    inv.cliente ||
    inv.receptor ||
    (customer
      ? {
          tipo_doc: customer.documentType || 2,
          cod_pais_doc: customer.documentCountry || 'UY',
          nro_doc: customer.documentNumber || '',
          denominacion: customer.name || '',
          direccion: customer.address || '',
          ciudad: customer.city || '',
          departamento: customer.state || '',
          pais: customer.country || 'URUGUAY',
        }
      : null);

  return {
    ...inv,
    serie: inv.serie || inv.series || 'A',
    numero: inv.numero ?? inv.number,
    tipo_comprobante: inv.tipo_comprobante || inv.invoiceType || 111,
    fecha_comprobante:
      inv.fecha_comprobante ||
      (inv.issueDate ? String(inv.issueDate).slice(0, 10) : ''),
    moneda: inv.moneda || inv.currency || 'UYU',
    items,
    cliente,
    totales: mapInvoiceTotals(inv),
  };
};

export default function CreateCreditNote({ onSubmit }) {
  const [form, setForm] = useState({
    tipo_comprobante: 112, // Tipo para nota de crédito
    moneda: 'UYU',
    fecha_comprobante: '',
    forma_pago: 1,
    cliente: {
      tipo_doc: 2,
      cod_pais_doc: 'UY',
      nro_doc: '',
      denominacion: '',
      direccion: '',
      ciudad: '',
      departamento: '',
      pais: 'URUGUAY',
    },
    items: [],
    adenda: { texto: '' },
    factura_referencia: {
      tipo: 111,
      serie: '',
      numero: '',
      fecha: '',
    },
  });

  const [customerSearch, setCustomerSearch] = useState('');
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [customerDropdown, setCustomerDropdown] = useState(false);
  const [invoiceDropdown, setInvoiceDropdown] = useState(false);
  const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);
  const [isSearchingInvoices, setIsSearchingInvoices] = useState(false);
  const [hasSearchedCustomers, setHasSearchedCustomers] = useState(false);
  const [hasSearchedInvoices, setHasSearchedInvoices] = useState(false);
  const [isAddCustomerModalOpen, setIsAddCustomerModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const toast = useToast();

  // Mapeos para unidades
  const unitDisplayMap = {
    'un': 'UNIDAD',
    'kg': 'KILO',
    'lt': 'LITRO',
    'm': 'METRO',
    'h': 'HORA',
    'min': 'MINUTO'
  };

  const unitValueMap = {
    'UNIDAD': 'un',
    'KILO': 'kg',
    'LITRO': 'lt',
    'METRO': 'm',
    'HORA': 'h',
    'MINUTO': 'min'
  };

  // Función para calcular totales basados en la factura original
  const calculateTotals = (items) => {
    if (!selectedInvoice || !selectedInvoice.totales || !selectedInvoice.items) {
      // Si no hay factura seleccionada, calcular normalmente
      const subtotal = items.reduce((sum, item) => sum + (item.cantidad * item.precio), 0);
      const ivaRate = 22.0;
      const ivaAmount = subtotal * (ivaRate / 100);
      const total = subtotal + ivaAmount;
      
      return {
        subtotal: parseFloat(subtotal.toFixed(2)),
        ivaRate,
        ivaAmount: parseFloat(ivaAmount.toFixed(2)),
        total: parseFloat(total.toFixed(2))
      };
    }

    // Calcular el total original de la factura (suma de todos los items originales)
    const originalTotal = selectedInvoice.items.reduce((sum, item) => 
      sum + (item.cantidad * item.precio), 0
    );

    // Calcular el total actual de la nota de crédito
    const currentTotal = items.reduce((sum, item) => 
      sum + (item.cantidad * item.precio), 0
    );

    // Calcular la proporción
    const proportion = originalTotal > 0 ? currentTotal / originalTotal : 0;

    // Usar los totales de la factura original y aplicar la proporción
    const originalTotals = selectedInvoice.totales;
    const subtotal = parseFloat((originalTotals.iva_basico * proportion).toFixed(2));
    const ivaAmount = parseFloat((originalTotals.mnt_iva_tasa_basico * proportion).toFixed(2));
    const total = parseFloat((originalTotals.total * proportion).toFixed(2));
    const ivaRate = originalTotals.tasa_iva_basico || 22.0;

    return {
      subtotal,
      ivaRate,
      ivaAmount,
      total
    };
  };

  // Función para obtener el texto de visualización de una unidad
  const getUnitDisplay = (unitValue) => {
    return unitDisplayMap[unitValue] || unitValue;
  };

  // Función para obtener el valor interno de una unidad
  const getUnitValue = (unitDisplay) => {
    return unitValueMap[unitDisplay] || unitDisplay;
  };

  // Configurar fecha actual al cargar el componente
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setForm((prev) => ({ ...prev, fecha_comprobante: today }));
  }, []);

  const handleChange = (field, value) => {
    const fields = field.split('.');
    if (fields.length === 1) {
      setForm((prev) => ({ ...prev, [field]: value }));
    } else if (fields.length === 2) {
      setForm((prev) => ({
        ...prev,
        [fields[0]]: { ...prev[fields[0]], [fields[1]]: value },
      }));
    } else if (fields.length === 3) {
      setForm((prev) => ({
        ...prev,
        [fields[0]]: {
          ...prev[fields[0]],
          [fields[1]]: { ...prev[fields[0]][fields[1]], [fields[2]]: value },
        },
      }));
    }
  };

  // Función para búsqueda de clientes en el backend
  const searchCustomersInBackend = async (query) => {
    if (!query.trim()) {
      setFilteredCustomers([]);
      setCustomerDropdown(false);
      setHasSearchedCustomers(false);
      return;
    }

    try {
      setIsSearchingCustomers(true);
      setHasSearchedCustomers(true);
      const response = await getData(
        `/invoices/customers?name=${encodeURIComponent(query)}`,
      );

      const customers = response?.payload?.customers || [];
      
      setFilteredCustomers(customers);
      setCustomerDropdown(true);
    } catch (error) {
      toast({
        title: 'Error al buscar clientes',
        description: error.message || 'No se pudieron cargar los clientes',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setFilteredCustomers([]);
      setCustomerDropdown(true);
    } finally {
      setIsSearchingCustomers(false);
    }
  };

  // Función para búsqueda de facturas por cliente
  const searchInvoicesByCustomer = async (customerDocument) => {
    if (!customerDocument.trim()) {
      setFilteredInvoices([]);
      setInvoiceDropdown(false);
      return;
    }

    try {
      setIsSearchingInvoices(true);
      const response = await getData(
        `/invoices/search/local?document=${encodeURIComponent(customerDocument)}&page=1&limit=50`,
      );

      const invoices = (response?.payload?.invoices || []).map(
        mapLocalInvoiceForCreditNote,
      );

      setFilteredInvoices(invoices);
      setInvoiceDropdown(true);
      
      if (invoices.length > 0) {
        toast({
          title: 'Facturas cargadas',
          description: `Se encontraron ${invoices.length} factura(s) del cliente`,
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Sin facturas',
          description: 'No se encontraron facturas para este cliente',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error al buscar facturas del cliente:', error);
      toast({
        title: 'Error al buscar facturas',
        description: error.message || 'No se pudieron cargar las facturas del cliente',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setFilteredInvoices([]);
      setInvoiceDropdown(false);
    } finally {
      setIsSearchingInvoices(false);
    }
  };

  // Función para búsqueda de facturas en el backend
  const searchInvoicesInBackend = async (query) => {
    if (!query.trim()) {
      setFilteredInvoices([]);
      setInvoiceDropdown(false);
      setHasSearchedInvoices(false);
      return;
    }

    try {
      setIsSearchingInvoices(true);
      setHasSearchedInvoices(true);
      const q = query.trim();
      const params = new URLSearchParams({ page: '1', limit: '20' });
      if (/\d/.test(q)) {
        params.set('invoiceNumber', q);
      } else {
        params.set('customer', q);
      }
      const response = await getData(`/invoices/search/local?${params}`);

      const invoices = (response?.payload?.invoices || []).map(
        mapLocalInvoiceForCreditNote,
      );

      setFilteredInvoices(invoices);
      setInvoiceDropdown(true);
    } catch (error) {
      toast({
        title: 'Error al buscar facturas',
        description: error.message || 'No se pudieron cargar las facturas',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setFilteredInvoices([]);
      setInvoiceDropdown(true);
    } finally {
      setIsSearchingInvoices(false);
    }
  };

  const handleCustomerKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchCustomersInBackend(customerSearch);
    }
  };

  const handleInvoiceKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchInvoicesInBackend(invoiceSearch);
    }
  };

  const selectInvoice = async (invoice) => {
    let mapped = mapLocalInvoiceForCreditNote(invoice);
    if ((!mapped.items || mapped.items.length === 0) && invoice.id) {
      try {
        const full = await getData(`/invoices/${invoice.id}`);
        mapped = mapLocalInvoiceForCreditNote(full?.payload || full);
      } catch (error) {
        toast({
          title: 'No se pudo cargar el detalle de la factura',
          description: error.message,
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
      }
    }

    setSelectedInvoice(mapped);
    setInvoiceSearch(
      `${mapped.serie || ''}-${mapped.numero ?? mapped.id}`,
    );

    handleChange('factura_referencia.tipo', mapped.tipo_comprobante || 111);
    handleChange('factura_referencia.serie', mapped.serie || '');
    handleChange('factura_referencia.numero', mapped.numero ?? mapped.id);
    handleChange('factura_referencia.fecha', mapped.fecha_comprobante || '');

    if (mapped.cliente) {
      const clienteData = mapped.cliente;
      setCustomerSearch(clienteData.denominacion || '');
      handleChange('cliente.nro_doc', clienteData.nro_doc || '');
      handleChange('cliente.denominacion', clienteData.denominacion || '');
      handleChange('cliente.direccion', clienteData.direccion || '');
      handleChange('cliente.ciudad', clienteData.ciudad || '');
      handleChange('cliente.departamento', clienteData.departamento || '');
    }

    if (mapped.items?.length > 0) {
      const creditNoteItems = mapped.items.map((item) => ({
        concepto: item.concepto,
        unidad: item.unidad,
        cantidad: item.cantidad,
        precio: item.precio,
        cod_item: item.cod_item || '',
        indicador_facturacion: item.indicador_facturacion || 3,
      }));

      setForm((prev) => ({ ...prev, items: creditNoteItems }));

      toast({
        title: 'Factura cargada',
        description: `Se cargaron ${creditNoteItems.length} producto(s) de la factura`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }

    setInvoiceDropdown(false);
    setHasSearchedInvoices(false);
  };

  // Funciones para manejar el modal de agregar cliente
  const openAddCustomerModal = () => {
    setIsAddCustomerModalOpen(true);
  };

  const closeAddCustomerModal = () => {
    setIsAddCustomerModalOpen(false);
  };

  const handleCustomerCreated = (newCustomer) => {
    const documentNumber = newCustomer.documentNumber || '';
    setCustomerSearch(newCustomer.name);
    handleChange('cliente.nro_doc', documentNumber);
    handleChange('cliente.denominacion', newCustomer.name || '');
    handleChange('cliente.direccion', newCustomer.address || '');
    handleChange('cliente.ciudad', newCustomer.city || '');
    handleChange('cliente.departamento', newCustomer.state || newCustomer.department || '');

    // Buscar automáticamente las facturas del cliente recién creado
    if (documentNumber) {
      searchInvoicesByCustomer(documentNumber);
    }

    toast({
      title: 'Cliente agregado exitosamente',
      description: `${newCustomer.name} ha sido seleccionado automáticamente`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  // Función para eliminar un item
  const removeItem = (index) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  // Función para actualizar cantidad, precio o código de un item
  const updateItem = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i === index) {
          // Si es código, mantener como string
          if (field === 'cod_item') {
            return { ...item, [field]: value };
          }
          // Si es cantidad o precio, parsear como número
          return { ...item, [field]: parseFloat(value) || 0 };
        }
        return item;
      }),
    }));
  };

  const buildPayload = (form) => {
    // Usar la función de cálculo consistente
    const totals = calculateTotals(form.items);

    // Construir payload según el formato requerido
    const payload = {
      tipo_comprobante: form.tipo_comprobante, // 112 para nota de crédito
      serie: selectedInvoice?.serie || "A", // Usar la misma serie de la factura original
      forma_pago: form.forma_pago,
      moneda: form.moneda,
      cod_montos_brutos: selectedInvoice?.cod_montos_brutos || 1,
      fecha_comprobante: form.fecha_comprobante,
      
      // Información del cliente
      cliente: {
        tipo_doc: form.cliente.tipo_doc || 2,
        cod_pais_doc: form.cliente.cod_pais_doc || "UY",
        nro_doc: form.cliente.nro_doc,
        denominacion: form.cliente.denominacion,
        direccion: form.cliente.direccion || "N/D",
        ciudad: form.cliente.ciudad || "N/D",
        departamento: form.cliente.departamento || "",
        pais: form.cliente.pais || "URUGUAY"
      },
      
      // Información del receptor (mismo que cliente para nota de crédito)
      receptor: {
        tipo_doc: form.cliente.tipo_doc || 2,
        cod_pais_doc: form.cliente.cod_pais_doc || "UY",
        nro_doc: form.cliente.nro_doc,
        denominacion: form.cliente.denominacion,
        direccion: form.cliente.direccion || "N/D",
        ciudad: form.cliente.ciudad || "N/D",
        departamento: form.cliente.departamento || ""
      },

      // Totales calculados basados en la factura original
      totales: {
        lineas: form.items.length,
        tasa_iva_basico: totals.ivaRate,
        iva_basico: totals.subtotal, // Monto base sin IVA
        mnt_iva_tasa_basico: totals.ivaAmount, // Monto del IVA
        total: totals.total, // Total con IVA
        total_pagar: totals.total
      },

      // Items con formato requerido
      items: form.items.map((item, index) => ({
        linea_nro: index + 1,
        indicador_facturacion: item.indicador_facturacion || 3,
        cantidad: parseFloat(item.cantidad),
        unidad: getUnitValue(item.unidad), // Convertir a formato interno (ej: "UNIDAD" -> "un")
        concepto: item.concepto,
        precio: parseFloat(item.precio)
      })),

      // Referencias a la factura original
      referencias: selectedInvoice ? [{
        linea_nro: 0,
        tipo_comprobante: form.factura_referencia.tipo || 111,
        serie: form.factura_referencia.serie || "A",
        numero: parseInt(form.factura_referencia.numero) || 0
      }] : [],

      // Información adicional
      adenda: {
        texto: form.adenda.texto || ""
      }
    };
    
    return payload;
  };

  const cardBg = useColorModeValue('white', 'gray.800');

  return (
    <Box p={{ base: 3, md: 4 }} bg={cardBg} borderRadius="md">
      <VStack spacing={4} align="stretch">
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
          <FormControl>
            <FormLabel fontSize="sm">Tipo de Comprobante</FormLabel>
            <Select size="sm"
              value={form.tipo_comprobante}
              onChange={(e) => handleChange('tipo_comprobante', parseInt(e.target.value))}
            >
              <option value={112}>112 - Nota de Crédito</option>
            </Select>
          </FormControl>
          
          <FormControl>
            <FormLabel fontSize="sm">Moneda</FormLabel>
            <Select size="sm"
              value={form.moneda}
              onChange={(e) => handleChange('moneda', e.target.value)}
            >
              <option value="UYU">UYU - Pesos Uruguayos</option>
              <option value="USD">USD - Dólares</option>
            </Select>
          </FormControl>
          
          <FormControl>
            <FormLabel fontSize="sm">Fecha</FormLabel>
            <Input size="sm" type="date"
              value={form.fecha_comprobante}
              onChange={(e) => handleChange('fecha_comprobante', e.target.value)}
            />
          </FormControl>
        </SimpleGrid>

        <Divider />
        <Heading size="md">Cliente</Heading>

        <FormControl>
          <FormLabel fontSize="sm">Buscar cliente</FormLabel>
          <HStack align="start">
            <Box flex="1" position="relative">
              <InputGroup>
                <Input
                  placeholder={
                    isSearchingCustomers
                      ? 'Buscando...'
                      : 'Nombre del cliente - presiona Enter'
                  }
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  onKeyPress={handleCustomerKeyPress}
                  isDisabled={isSearchingCustomers}
                  onBlur={() =>
                    setTimeout(() => {
                      setCustomerDropdown(false);
                      setHasSearchedCustomers(false);
                    }, 150)
                  }
                />
                <InputRightElement pointerEvents="none">
                  <ChevronDownIcon />
                </InputRightElement>
              </InputGroup>
              {customerDropdown &&
                (filteredCustomers.length > 0 ||
                  isSearchingCustomers ||
                  (hasSearchedCustomers && filteredCustomers.length === 0)) && (
                  <List
                    bg="white"
                    border="1px solid #ccc"
                    mt={1}
                    borderRadius="md"
                    maxHeight="150px"
                    overflowY="auto"
                    position="absolute"
                    zIndex={10}
                    w="100%"
                  >
                    {filteredCustomers.length ? (
                      filteredCustomers.map((c) => (
                        <ListItem
                          key={c.id}
                          px={4}
                          py={2}
                          _hover={{ bg: 'gray.100' }}
                          cursor="pointer"
                          onMouseDown={() => {
                            setCustomerSearch(c.name);
                            const document = c.document || c.documentNumber || c.nro_doc || c.nro_documento || '';
                            const department = c.department || c.departamento || c.estado || c.state || '';
                            handleChange('cliente.nro_doc', document);
                            handleChange('cliente.denominacion', c.name || '');
                            handleChange('cliente.direccion', c.address || '');
                            handleChange('cliente.ciudad', c.city || '');
                            handleChange('cliente.departamento', department);
                            setCustomerDropdown(false);
                            setHasSearchedCustomers(false);
                            
                            // Buscar automáticamente las facturas del cliente seleccionado
                            if (document) {
                              searchInvoicesByCustomer(document);
                            }
                          }}
                        >
                          <Box>
                            <Text fontWeight="medium">{c.name}</Text>
                            {(c.document || c.documentNumber || c.nro_doc || c.nro_documento) && (
                              <Text fontSize="sm" color="gray.600">
                                Doc: {c.document || c.documentNumber || c.nro_doc || c.nro_documento}
                              </Text>
                            )}
                            {(c.department || c.departamento || c.estado || c.state) && (
                              <Text fontSize="sm" color="blue.600">
                                Depto: {c.department || c.departamento || c.estado || c.state}
                              </Text>
                            )}
                          </Box>
                        </ListItem>
                      ))
                    ) : isSearchingCustomers ? (
                      <ListItem px={4} py={2} color="blue.500">
                        Buscando clientes...
                      </ListItem>
                    ) : (
                      <ListItem px={4} py={2} color="gray.500">
                        No se encontraron clientes
                      </ListItem>
                    )}
                  </List>
                )}
            </Box>

            <Button onClick={openAddCustomerModal} minW="40px">
              <AddIcon />
            </Button>
          </HStack>
        </FormControl>

        {/* Campos del cliente */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <FormControl>
            <FormLabel fontSize="sm">Documento del cliente</FormLabel>
            <Input
              value={form.cliente.nro_doc}
              onChange={(e) => handleChange('cliente.nro_doc', e.target.value)}
              placeholder="Ingrese documento del cliente"
              readOnly
            />
          </FormControl>
        </SimpleGrid>

        <Divider />
        <HStack justify="space-between" align="center">
          <Heading size="md">Factura de Referencia</Heading>
          {form.cliente.nro_doc && (
            <Button
              size="sm"
              colorScheme="gray"
              variant="outline"
              onClick={() => searchInvoicesByCustomer(form.cliente.nro_doc)}
              isLoading={isSearchingInvoices}
              loadingText="Buscando..."
            >
              🔄 Refrescar facturas del cliente
            </Button>
          )}
        </HStack>

        <FormControl>
          <FormLabel fontSize="sm">Buscar factura</FormLabel>
          <Box position="relative">
            <InputGroup>
              <Input
                placeholder={
                  isSearchingInvoices
                    ? 'Buscando...'
                    : filteredInvoices.length > 0 && form.cliente.nro_doc
                    ? `Selecciona una de las ${filteredInvoices.length} facturas del cliente`
                    : 'Número de factura o datos del cliente - presiona Enter'
                }
                value={invoiceSearch}
                onChange={(e) => setInvoiceSearch(e.target.value)}
                onKeyPress={handleInvoiceKeyPress}
                onFocus={() => {
                  // Si ya hay facturas del cliente cargadas, mostrar el dropdown
                  if (filteredInvoices.length > 0 && form.cliente.nro_doc) {
                    setInvoiceDropdown(true);
                  }
                }}
                isDisabled={isSearchingInvoices}
                onBlur={() =>
                  setTimeout(() => {
                    setInvoiceDropdown(false);
                    setHasSearchedInvoices(false);
                  }, 150)
                }
              />
              <InputRightElement pointerEvents="none">
                <ChevronDownIcon />
              </InputRightElement>
            </InputGroup>
            {invoiceDropdown &&
              (filteredInvoices.length > 0 ||
                isSearchingInvoices ||
                (hasSearchedInvoices && filteredInvoices.length === 0)) && (
                <List
                  bg="white"
                  border="1px solid #ccc"
                  mt={1}
                  borderRadius="md"
                  maxHeight="200px"
                  overflowY="auto"
                  position="absolute"
                  zIndex={10}
                  w="100%"
                >
                  {filteredInvoices.length ? (
                    filteredInvoices.map((invoice) => (
                      <ListItem
                        key={invoice.id}
                        px={4}
                        py={2}
                        _hover={{ bg: 'gray.100' }}
                        cursor="pointer"
                        onMouseDown={() => selectInvoice(invoice)}
                      >
                        <Box>
                          <Text fontWeight="medium">
                            Factura: {invoice.serie || invoice.series || ''}-
                            {invoice.numero ?? invoice.number ?? invoice.id}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            Cliente:{' '}
                            {invoice.cliente?.denominacion ||
                              invoice.customer?.name ||
                              'Sin nombre'}
                          </Text>
                          <Text fontSize="sm" color="blue.600">
                            Fecha:{' '}
                            {invoice.fecha_comprobante || invoice.issueDate
                              ? new Date(
                                  invoice.fecha_comprobante || invoice.issueDate,
                                ).toLocaleDateString('es-UY')
                              : '—'}
                          </Text>
                          <Text fontSize="sm" color="green.600">
                            Total: {invoice.moneda || invoice.currency || 'UYU'} $
                            {(
                              invoice.totales?.total ??
                              invoice.invoiceTotals?.total ??
                              invoice.total ??
                              0
                            ).toString()}
                          </Text>
                          {(invoice.items?.length || invoice.invoiceItems?.length) > 0 && (
                            <Text fontSize="xs" color="gray.500">
                              {invoice.items?.length || invoice.invoiceItems?.length}{' '}
                              producto(s)
                            </Text>
                          )}
                        </Box>
                      </ListItem>
                    ))
                  ) : isSearchingInvoices ? (
                    <ListItem px={4} py={2} color="blue.500">
                      Buscando facturas...
                    </ListItem>
                  ) : (
                    <ListItem px={4} py={2} color="gray.500">
                      No se encontraron facturas
                    </ListItem>
                  )}
                </List>
              )}
          </Box>
        </FormControl>

        {/* Información sobre facturas del cliente */}
        {form.cliente.nro_doc && filteredInvoices.length > 0 && !invoiceDropdown && (
          <Box p={3} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
            <Text fontSize="sm" color="green.700" fontWeight="medium">
              ✅ Se encontraron {filteredInvoices.length} factura(s) del cliente
            </Text>
            <Text fontSize="xs" color="green.600">
              Haz clic en el campo de búsqueda arriba para ver todas las facturas disponibles
            </Text>
          </Box>
        )}

        {selectedInvoice && (
          <Box p={4} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
            <Text fontWeight="medium" color="blue.800">
              📄 Factura seleccionada: {selectedInvoice.serie || ''}-{selectedInvoice.numero || selectedInvoice.id}
            </Text>
            <Text fontSize="sm" color="blue.600">
              Fecha: {new Date(selectedInvoice.fecha_comprobante).toLocaleDateString('es-UY')} | 
              Total: {selectedInvoice.moneda} ${
                selectedInvoice.totales?.total?.toFixed(2) || 
                selectedInvoice.total?.toFixed(2) || 
                '0.00'
              }
            </Text>
          </Box>
        )}

        <Divider />
        <Heading size="md">Productos a Acreditar</Heading>

        {form.items.length === 0 ? (
          <Box p={4} bg="gray.50" borderRadius="md" textAlign="center">
            <Text color="gray.500">
              Selecciona una factura para cargar automáticamente sus productos
            </Text>
          </Box>
        ) : (
          <VStack spacing={3} align="stretch">
            {form.items.map((item, index) => (
              <Box
                key={index}
                p={4}
                bg="gray.50"
                borderRadius="md"
                border="1px solid"
                borderColor="gray.200"
              >
                <SimpleGrid columns={{ base: 1, md: 6 }} spacing={3} alignItems="center">
                  <Box>
                    <Text fontSize="sm" color="gray.600">Código</Text>
                    <Input
                      value={item.cod_item || ''}
                      onChange={(e) => updateItem(index, 'cod_item', e.target.value)}
                      size="sm"
                      placeholder="Código"
                    />
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.600">Producto</Text>
                    <Text fontWeight="medium">{item.concepto}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.600">Unidad</Text>
                    <Text>{getUnitDisplay(item.unidad)}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.600">Cantidad</Text>
                    <NumberInput
                      value={item.cantidad}
                      onChange={(value) => updateItem(index, 'cantidad', value)}
                      min={0}
                      step={0.01}
                      size="sm"
                    >
                      <NumberInputField />
                    </NumberInput>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.600">Precio Unitario</Text>
                    <NumberInput
                      value={item.precio}
                      onChange={(value) => updateItem(index, 'precio', value)}
                      min={0}
                      step={0.01}
                      size="sm"
                    >
                      <NumberInputField />
                    </NumberInput>
                  </Box>
                  <Box textAlign="center">
                    <Text fontSize="sm" color="gray.600">Subtotal</Text>
                    <Text fontWeight="medium" color="green.600">
                      ${(item.cantidad * item.precio).toFixed(2)}
                    </Text>
                    <IconButton
                      size="sm"
                      colorScheme="red"
                      variant="ghost"
                      icon={<DeleteIcon />}
                      onClick={() => removeItem(index)}
                      mt={1}
                    />
                  </Box>
                </SimpleGrid>
              </Box>
            ))}
          </VStack>
        )}

        <FormControl>
          <FormLabel fontSize="sm">Observaciones</FormLabel>
          <Textarea
            value={form.adenda.texto}
            onChange={(e) => handleChange('adenda.texto', e.target.value)}
            placeholder="Motivo de la nota de crédito..."
            rows={3}
          />
        </FormControl>

        <Box p={4} bg="gray.50" borderRadius="md">
          <VStack spacing={3} align="stretch">
            {selectedInvoice && selectedInvoice.totales && (
              <VStack spacing={3}>
                <Box p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                  <Text fontSize="sm" color="blue.700" fontWeight="medium">
                    📊 Referencia de la factura original:
                  </Text>
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2} mt={2}>
                    <Box>
                      <Text fontSize="xs" color="blue.600">Subtotal original:</Text>
                      <Text fontSize="sm" fontWeight="medium">${selectedInvoice.totales.iva_basico?.toFixed(2) || '0.00'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="blue.600">IVA original:</Text>
                      <Text fontSize="sm" fontWeight="medium">${selectedInvoice.totales.mnt_iva_tasa_basico?.toFixed(2) || '0.00'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="blue.600">Total original:</Text>
                      <Text fontSize="sm" fontWeight="medium">${selectedInvoice.totales.total?.toFixed(2) || '0.00'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="blue.600">Tasa IVA:</Text>
                      <Text fontSize="sm" fontWeight="medium">{selectedInvoice.totales.tasa_iva_basico || 22}%</Text>
                    </Box>
                  </SimpleGrid>
                </Box>
                
                {(() => {
                  const originalItemsTotal = selectedInvoice.items?.reduce((sum, item) => sum + (item.cantidad * item.precio), 0) || 0;
                  const currentItemsTotal = form.items.reduce((sum, item) => sum + (item.cantidad * item.precio), 0);
                  const proportion = originalItemsTotal > 0 ? (currentItemsTotal / originalItemsTotal * 100) : 0;
                  
                  return (
                    <Box p={3} bg="yellow.50" borderRadius="md" border="1px solid" borderColor="yellow.200">
                      <Text fontSize="sm" color="yellow.800" fontWeight="medium">
                        🧮 Cálculo proporcional: {proportion.toFixed(1)}% de la factura original
                      </Text>
                      <Text fontSize="xs" color="yellow.700" mt={1}>
                        Los totales se calculan proporcionalmente basados en los montos originales de la factura
                      </Text>
                    </Box>
                  );
                })()}
              </VStack>
            )}
            
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <Box>
                <Text fontSize="sm" color="gray.600">Total de productos:</Text>
                <Text fontWeight="medium">{form.items.length}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600">Subtotal (sin IVA):</Text>
                <Text fontWeight="medium" color="blue.600">
                  ${calculateTotals(form.items).subtotal.toFixed(2)}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600">
                  IVA ({calculateTotals(form.items).ivaRate}%):
                </Text>
                <Text fontWeight="medium" color="orange.600">
                  ${calculateTotals(form.items).ivaAmount.toFixed(2)}
                </Text>
              </Box>
            </SimpleGrid>
            <Divider />
            <Box textAlign="center">
              <Text fontSize="sm" color="gray.600">Total a acreditar (IVA incluido):</Text>
              <Text fontSize="xl" fontWeight="bold" color="red.600">
                ${calculateTotals(form.items).total.toFixed(2)}
              </Text>
            </Box>
          </VStack>
        </Box>

        <Button
          colorScheme="red"
          onClick={() => {
            const payload = buildPayload(form);
            onSubmit(payload);
          }}
          isDisabled={form.items.length === 0 || !selectedInvoice}
        >
          Generar Nota de Crédito
        </Button>
      </VStack>

      {/* Modal para agregar nuevo cliente */}
      <AddCustomerModal
        isOpen={isAddCustomerModalOpen}
        onClose={closeAddCustomerModal}
        onCustomerCreated={handleCustomerCreated}
      />
    </Box>
  );
}
